import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined"
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined"
import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined"
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined"
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined"
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined"
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined"
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined"
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined"
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined"
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Avatar from "@mui/material/Avatar"
import Button from "@mui/material/Button"
import ButtonGroup from "@mui/material/ButtonGroup"
import Chip from "@mui/material/Chip"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import IconButton from "@mui/material/IconButton"
import Paper from "@mui/material/Paper"
import Stack from "@mui/material/Stack"
import TextField from "@mui/material/TextField"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import { useEffect, useMemo, useState } from "react"
import { Link as RouterLink } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { formatDateTime, formatPhone, useIsMobile } from "../../../common/utils"
import DialogHeader from "../components/DialogHeader"
import { phoneDisplay } from "../components/NameWithContactActionsCell"
import { PriorityCircleIcon } from "../components/TaskDialog"
import { useDialogContainer } from "../utils"
import { TASK_PRIORITIES, TASK_TYPES } from "./utils"

/**
 * Recruiter-focused task side panel: profile + notes + complete / schedule / next.
 * Requested by Kyle (in-house recruiter feedback).
 */
export default function TaskWorkDrawer({
  open,
  onClose,
  task,
  tasks,
  onTaskUpdated,
  onEditTask,
  onScheduleNewTask,
  onSelectTask,
  onCall,
  onSMS,
  onEmail,
  onSnackbar,
}) {
  const [profileNotes, setProfileNotes] = useState("")
  const [isSavingNotes, setIsSavingNotes] = useState(false)
  const [isCompleting, setIsCompleting] = useState(false)
  const axios = useAuthorizedAxiosClient()
  const isMobile = useIsMobile()
  const Container = useDialogContainer()

  const candidate = task?.candidate
  const hasPhone = Boolean(phoneDisplay(candidate?.phone))
  const hasEmail = Boolean(candidate?.email?.trim())
  const notesDirty = (candidate?.notes || "") !== profileNotes

  const { currentIndex, previousTask, nextTask } = useMemo(() => {
    if (!task || !tasks?.length) {
      return { currentIndex: -1, previousTask: null, nextTask: null }
    }
    const index = tasks.findIndex((t) => t.id === task.id)
    return {
      currentIndex: index,
      previousTask: index > 0 ? tasks[index - 1] : null,
      nextTask:
        index >= 0 && index < tasks.length - 1 ? tasks[index + 1] : null,
    }
  }, [task, tasks])

  const nextIncompleteTask = useMemo(() => {
    if (!task || !tasks?.length) {
      return null
    }
    const index = tasks.findIndex((t) => t.id === task.id)
    if (index < 0) {
      return null
    }
    return (
      tasks.slice(index + 1).find((t) => !t.completed) ||
      tasks.slice(0, index).find((t) => !t.completed) ||
      null
    )
  }, [task, tasks])

  useEffect(() => {
    if (!open || !candidate) {
      return
    }
    setProfileNotes(candidate.notes || "")
  }, [open, candidate?.id, candidate?.notes])

  function handleSoftClose() {
    onClose()
  }

  function handleSaveProfileNotes() {
    if (!candidate?.id) {
      return
    }
    setIsSavingNotes(true)
    const formData = new FormData()
    formData.append("notes", profileNotes)
    axios
      .patch(`/crm/api/candidates/${candidate.id}/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(({ data }) => {
        onTaskUpdated({
          ...task,
          candidate: {
            ...task.candidate,
            notes: data.notes ?? profileNotes,
          },
        })
        onSnackbar?.("Notes saved")
      })
      .finally(() => setIsSavingNotes(false))
  }

  function findNextIncompleteAfter(completedTaskId) {
    const index = tasks.findIndex((t) => t.id === completedTaskId)
    if (index < 0) {
      return null
    }
    return (
      tasks
        .slice(index + 1)
        .find((t) => t.id !== completedTaskId && !t.completed) ||
      tasks
        .slice(0, index)
        .find((t) => t.id !== completedTaskId && !t.completed) ||
      null
    )
  }

  function handleCompleteTask({ advance = true } = {}) {
    if (!task) {
      return
    }
    if (task.completed) {
      if (advance && nextIncompleteTask) {
        onSelectTask(nextIncompleteTask)
      }
      return
    }
    setIsCompleting(true)
    axios
      .patch(`/crm/api/tasks/${task.id}/`, { completed: true })
      .then(({ data }) => {
        onTaskUpdated({ ...task, ...data })
        onSnackbar?.("Task completed")
        if (advance) {
          const target = findNextIncompleteAfter(task.id)
          if (target) {
            onSelectTask(target)
          }
        }
      })
      .finally(() => setIsCompleting(false))
  }

  function handleCompleteAndSchedule() {
    if (!task) {
      return
    }
    if (task.completed) {
      onScheduleNewTask(task)
      return
    }
    setIsCompleting(true)
    axios
      .patch(`/crm/api/tasks/${task.id}/`, { completed: true })
      .then(({ data }) => {
        const updated = { ...task, ...data, completed: true }
        onTaskUpdated(updated)
        onSnackbar?.("Task completed")
        onScheduleNewTask(updated)
      })
      .finally(() => setIsCompleting(false))
  }

  if (!task) {
    return null
  }

  return (
    <Container
      open={open}
      onClose={handleSoftClose}
      anchor="right"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480 },
          maxWidth: "100%",
          bgcolor: "colors.gray50",
        },
      }}
    >
      <DialogHeader title="Task" onClose={handleSoftClose} />
      <DialogContent
        sx={{
          px: 2,
          py: 2,
          display: "flex",
          flexDirection: "column",
          bgcolor: "colors.gray50",
        }}
      >
        <Column sx={{ gap: 2, flex: 1, minHeight: 0, pb: 1 }}>
          <Row
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              px: 0.5,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
            >
              {currentIndex >= 0
                ? `Task ${currentIndex + 1} of ${tasks.length}`
                : "Task"}
            </Typography>
            <ButtonGroup
              size="small"
              variant="outlined"
              sx={{ bgcolor: "common.white" }}
            >
              <Tooltip title="Previous task">
                <span>
                  <IconButton
                    size="small"
                    disabled={!previousTask}
                    onClick={() => previousTask && onSelectTask(previousTask)}
                  >
                    <ArrowBackIosNewOutlinedIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Next task">
                <span>
                  <IconButton
                    size="small"
                    disabled={!nextTask}
                    onClick={() => nextTask && onSelectTask(nextTask)}
                  >
                    <ArrowForwardIosOutlinedIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
            </ButtonGroup>
          </Row>

          <SectionCard
            icon={
              <PersonOutlineOutlinedIcon fontSize="small" color="primary" />
            }
            title="Contact"
            action={
              <Button
                component={RouterLink}
                to={`/candidates/${candidate?.id}`}
                size="small"
                endIcon={
                  <OpenInNewOutlinedIcon sx={{ fontSize: "14px !important" }} />
                }
              >
                Full profile
              </Button>
            }
          >
            <Row sx={{ gap: 1.5, alignItems: "center" }}>
              <Avatar
                src={candidate?.profile_image || undefined}
                alt={candidate?.candidate_name}
                sx={{ width: 52, height: 52 }}
              />
              <Column sx={{ minWidth: 0, flex: 1, gap: 0.75 }}>
                <Typography
                  variant="h4"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {candidate?.candidate_name || "Unknown"}
                </Typography>
                <Row sx={{ gap: 0.5 }}>
                  <ContactIconButton
                    title={hasPhone ? "Call" : "No phone number"}
                    disabled={!hasPhone}
                    onClick={(e) => onCall?.(e, candidate)}
                  >
                    <PhoneOutlinedIcon fontSize="small" />
                  </ContactIconButton>
                  <ContactIconButton
                    title={hasPhone ? "SMS" : "No phone number"}
                    disabled={!hasPhone}
                    onClick={(e) => onSMS?.(e, candidate)}
                  >
                    <SmsOutlinedIcon fontSize="small" />
                  </ContactIconButton>
                  <ContactIconButton
                    title={hasEmail ? "Email" : "No email"}
                    disabled={!hasEmail}
                    onClick={(e) => onEmail?.(e, candidate)}
                  >
                    <EmailOutlinedIcon fontSize="small" />
                  </ContactIconButton>
                </Row>
              </Column>
            </Row>

            <Stack
              spacing={1}
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: "colors.gray50",
                border: "1px solid",
                borderColor: "colors.gray200",
              }}
            >
              <InfoRow
                icon={<PhoneOutlinedIcon fontSize="small" color="action" />}
                label="Phone"
                value={
                  phoneDisplay(candidate?.phone)
                    ? formatPhone(candidate.phone)
                    : "—"
                }
              />
              <InfoRow
                icon={<EmailOutlinedIcon fontSize="small" color="action" />}
                label="Email"
                value={candidate?.email || "—"}
              />
              <InfoRow
                icon={<BusinessOutlinedIcon fontSize="small" color="action" />}
                label="Brokerage"
                value={candidate?.company_name || "—"}
              />
            </Stack>

            <Column sx={{ gap: 1 }}>
              <Row
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  Notes
                </Typography>
                <LoadingButton
                  size="small"
                  variant={notesDirty ? "contained" : "outlined"}
                  loading={isSavingNotes}
                  disabled={!notesDirty}
                  onClick={handleSaveProfileNotes}
                >
                  Save
                </LoadingButton>
              </Row>
              <TextField
                value={profileNotes}
                onChange={(e) => setProfileNotes(e.target.value)}
                multiline
                minRows={4}
                maxRows={8}
                fullWidth
                placeholder="Add notes about this person…"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "colors.gray50",
                  },
                }}
              />
            </Column>
          </SectionCard>

          <SectionCard
            icon={<AssignmentOutlinedIcon fontSize="small" color="primary" />}
            title="Current task"
            action={
              <IconButton size="small" onClick={() => onEditTask(task)}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            }
          >
            <Typography variant="body1" fontWeight={700}>
              {task.title}
            </Typography>

            <Stack
              spacing={1.25}
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: "colors.gray50",
                border: "1px solid",
                borderColor: "colors.gray200",
              }}
            >
              <TaskMetaRow label="Type">
                <TaskTypeChip type={task.type} />
              </TaskMetaRow>
              <TaskMetaRow label="Priority">
                <Row sx={{ gap: 0.75, alignItems: "center" }}>
                  <PriorityCircleIcon
                    priority={TASK_PRIORITIES[task.priority]}
                    sx={{ width: 12, height: 12 }}
                  />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ textTransform: "capitalize" }}
                  >
                    {TASK_PRIORITIES[task.priority] || task.priority}
                  </Typography>
                </Row>
              </TaskMetaRow>
              <TaskMetaRow label="Due">
                <Typography variant="body2" fontWeight={500}>
                  {formatDateTime(task.due_datetime)}
                </Typography>
              </TaskMetaRow>
              {task.completed ? (
                <TaskMetaRow label="Status">
                  <Chip
                    size="small"
                    color="success"
                    label="Completed"
                    sx={{ fontWeight: 600 }}
                  />
                </TaskMetaRow>
              ) : null}
            </Stack>

            <NoteBlock label="Task notes" emptyText="No notes on this task">
              {task.notes || null}
            </NoteBlock>
          </SectionCard>
        </Column>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2,
          py: 2,
          gap: 1,
          flexWrap: "wrap",
          justifyContent: "stretch",
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "common.white",
        }}
      >
        <LoadingButton
          variant="contained"
          color="primary"
          loading={isCompleting}
          disabled={task.completed}
          startIcon={<CheckCircleOutlineOutlinedIcon />}
          onClick={() => handleCompleteTask({ advance: true })}
          sx={{ flex: { xs: "1 1 100%", sm: "1 1 auto" } }}
        >
          {task.completed ? "Completed" : "Complete"}
        </LoadingButton>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<AddCircleOutlineOutlinedIcon />}
          onClick={handleCompleteAndSchedule}
          sx={{ flex: { xs: "1 1 45%", sm: "1 1 auto" } }}
        >
          {task.completed ? "New task" : "Complete & new"}
        </Button>
        <Button
          variant="outlined"
          color="inherit"
          disabled={!nextIncompleteTask || nextIncompleteTask.id === task.id}
          endIcon={<ArrowForwardIosOutlinedIcon fontSize="inherit" />}
          onClick={() => nextIncompleteTask && onSelectTask(nextIncompleteTask)}
          sx={{ flex: { xs: "1 1 45%", sm: "1 1 auto" } }}
        >
          Next
        </Button>
      </DialogActions>
    </Container>
  )
}

function SectionCard({ icon, title, subtitle, action, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: "common.white",
        border: "1px solid",
        borderColor: "colors.gray200",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <Row
        sx={{
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 1,
        }}
      >
        <Row sx={{ gap: 1, alignItems: "flex-start", minWidth: 0 }}>
          <Row
            sx={{
              mt: 0.15,
              width: 28,
              height: 28,
              borderRadius: 1,
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "colors.brand50",
              flexShrink: 0,
            }}
          >
            {icon}
          </Row>
          <Column sx={{ minWidth: 0, gap: 0.25 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            ) : null}
          </Column>
        </Row>
        {action || null}
      </Row>
      {children}
    </Paper>
  )
}

function TaskMetaRow({ label, children }) {
  return (
    <Row sx={{ gap: 1.25, alignItems: "center" }}>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        sx={{ width: 64, flexShrink: 0 }}
      >
        {label}
      </Typography>
      {children}
    </Row>
  )
}

function TaskTypeChip({ type }) {
  const config = TASK_TYPE_DISPLAY[type] || {
    label: TASK_TYPES[type] || type,
    icon: <AssignmentOutlinedIcon />,
    color: "default",
  }
  return (
    <Chip
      size="small"
      icon={config.icon}
      label={config.label}
      color={config.color}
      variant={config.color === "default" ? "outlined" : "filled"}
      sx={{ fontWeight: 600 }}
    />
  )
}

const TASK_TYPE_DISPLAY = {
  email: {
    label: "Email",
    icon: <EmailOutlinedIcon />,
    color: "secondary",
  },
  sms: {
    label: "SMS",
    icon: <SmsOutlinedIcon />,
    color: "info",
  },
  call: {
    label: "Call",
    icon: <PhoneOutlinedIcon />,
    color: "primary",
  },
  todo: {
    label: "To-do",
    icon: <CheckBoxOutlinedIcon />,
    color: "default",
  },
}

function NoteBlock({ label, emptyText, children }) {
  const hasContent = Boolean(children)
  return (
    <Column
      sx={{
        gap: 0.75,
        p: 1.5,
        borderRadius: 1.5,
        bgcolor: "colors.brand50",
        border: "1px solid",
        borderColor: "colors.brand200",
      }}
    >
      <Typography
        variant="caption"
        fontWeight={700}
        color="primary.main"
        sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        color={hasContent ? "text.primary" : "text.secondary"}
        sx={{
          whiteSpace: "pre-wrap",
          fontStyle: hasContent ? "normal" : "italic",
        }}
      >
        {hasContent ? children : emptyText}
      </Typography>
    </Column>
  )
}

function ContactIconButton({ title, disabled, onClick, children }) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          color="primary"
          disabled={disabled}
          onClick={onClick}
          sx={{
            border: "1px solid",
            borderColor: "colors.brand200",
            bgcolor: "colors.brand50",
            borderRadius: 1,
            "&:hover": { bgcolor: "colors.brand100" },
          }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  )
}

function InfoRow({ icon, label, value }) {
  return (
    <Row sx={{ gap: 1.25, alignItems: "center" }}>
      {icon}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ width: 72, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={500}
        sx={{
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={typeof value === "string" ? value : undefined}
      >
        {value}
      </Typography>
    </Row>
  )
}
